/* 100mL D2C — global behaviors (no dependencies) */

(function () {
  'use strict';

  var allowMotion = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------------
     Section lifecycle.

     The theme editor swaps a single section's markup in and out without a page
     load. Anything that binds to elements at startup is therefore dead the moment
     a merchant edits that section — it holds references to nodes that are no
     longer in the document, and the replacement nodes have no listeners. Nothing
     in this file handled that before, so every interactive section went dead on
     first edit until the merchant reloaded.

       initSection(fn)   — fn(scope) runs once now for the whole document, and
                           again for any section the editor (re)loads, scoped to
                           just that section.
       onTeardown(el,fn) — release a timer/observer when el's section is removed.
       initOnce(el,key)  — true the first time a given element is initialised, so
                           a re-run can never double-bind.

     Handlers already delegated on `document` (quantity steppers, collection
     facets, drawer clicks, add-to-cart) survive re-render by construction and are
     deliberately NOT registered here. */
  var sectionInits = [];
  var teardowns = [];

  function initOnce(el, key) {
    var attr = 'data-init-' + key;
    if (el.getAttribute(attr) === '1') return false;
    el.setAttribute(attr, '1');
    return true;
  }

  function onTeardown(el, fn) {
    teardowns.push({ el: el, fn: fn });
  }

  function initSection(fn) {
    sectionInits.push(fn);
    fn(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    sectionInits.forEach(function (fn) {
      /* One section's failure must not stop the rest from initialising. */
      try { fn(event.target); } catch (error) { /* ignore */ }
    });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    teardowns = teardowns.filter(function (entry) {
      if (!event.target.contains(entry.el)) return true;
      try { entry.fn(); } catch (error) { /* ignore */ }
      return false;
    });
  });

  /* Block select/deselect. Where a block maps to a pane the shopper only sees one
     of at a time — a slide, a tab, an accordion answer — selecting it in the editor
     has to reveal it, or the merchant edits a block they cannot see. A section
     opts in by calling onBlockSelect on its root; the listener walks up from the
     block to find the nearest owner. */
  function onBlockSelect(el, select, deselect) {
    el.blockSelect = select;
    el.blockDeselect = deselect;
  }

  function dispatchBlockEvent(block, prop) {
    var node = block;
    while (node && node.nodeType === 1) {
      if (typeof node[prop] === 'function') {
        try { node[prop](block); } catch (error) { /* ignore */ }
        return;
      }
      node = node.parentElement;
    }
  }

  document.addEventListener('shopify:block:select', function (event) {
    /* Generic: a block that IS (or wraps) a <details> just opens. Covers the FAQ
       accordion, which needs no section-specific wiring. */
    var details = event.target.matches('details') ? event.target : event.target.querySelector('details');
    if (details) details.open = true;
    dispatchBlockEvent(event.target, 'blockSelect');
  });

  document.addEventListener('shopify:block:deselect', function (event) {
    dispatchBlockEvent(event.target, 'blockDeselect');
  });

  /* Format cents using the shop's money_format so JS totals match Liquid's
     `money` filter (thousands separators, decimals, currency symbol). Shared by
     the PDP quantity total and the pouch builder. */
  function formatMoney(cents) {
    var format = (window.theme && window.theme.moneyFormat) || '${{amount}}';
    format = format.replace(/<[^>]*>/g, '');
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      return dollars + (parts[1] ? decimal + parts[1] : '');
    }
    var match = format.match(placeholder);
    if (!match) return format;
    var value;
    switch (match[1]) {
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' ', ''); break;
      default: value = withDelimiters(cents, 2); break;
    }
    return format.replace(placeholder, value);
  }

  /* Scroll reveals — fade + rise elements into view as they enter the viewport.
     JS-gated: html.js-reveal is only added when motion is allowed and
     IntersectionObserver exists, so content is never left hidden otherwise. */
  var revealObserver = null;
  if (allowMotion && 'IntersectionObserver' in window) {
    revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-inview');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
  }
  initSection(function (scope) {
    if (!revealObserver) return;
    var revealEls = scope.querySelectorAll('[data-reveal], [data-reveal-group]');
    if (!revealEls.length) return;
    document.documentElement.classList.add('js-reveal');
    revealEls.forEach(function (el) {
      if (initOnce(el, 'reveal')) revealObserver.observe(el);
    });
  });

  /* Compact floating header: once the full two-row header has scrolled past, it
     collapses into a single floating bar that stays for the rest of the page.
     The spacer holds its place in the flow, so lifting the header out doesn't
     shift the content underneath. Entering and leaving use separate thresholds
     — the bar arrives below the header and leaves once it's back at its own
     resting position — so the two can never fight over a single scroll pixel. */
  var siteHeader = document.querySelector('[data-header]');
  var headerSpacer = document.querySelector('[data-header-spacer]');
  if (siteHeader && headerSpacer) {
    var HEADER_EXIT = 200;
    var headerIsCompact = false;
    var headerExitTimer = null;
    var headerArriveTimer = null;
    var headerTop = 0;
    var headerFullHeight = 0;

    /* The compact bar is fixed and shorter, so the natural height can only be
       read with the class off. Toggling it back within the same task means the
       browser never paints the intermediate state. */
    var measureHeader = function () {
      var wasCompact = siteHeader.classList.contains('is-compact');
      if (wasCompact) {
        headerSpacer.style.height = '';
        siteHeader.classList.remove('is-compact');
      }
      headerFullHeight = siteHeader.offsetHeight;
      headerTop = siteHeader.getBoundingClientRect().top + window.scrollY;
      if (wasCompact) {
        siteHeader.classList.add('is-compact');
        headerSpacer.style.height = headerFullHeight + 'px';
      }
    };

    var enterCompact = function (animate) {
      if (headerIsCompact) return;
      headerIsCompact = true;
      if (headerExitTimer) {
        clearTimeout(headerExitTimer);
        headerExitTimer = null;
      }
      siteHeader.classList.remove('is-leaving');
      /* Already fixed and on screen — the exit was caught mid-flight. */
      if (siteHeader.classList.contains('is-compact')) return;
      measureHeader();
      headerSpacer.style.height = headerFullHeight + 'px';
      siteHeader.classList.add('is-compact');
      if (animate && allowMotion) {
        siteHeader.classList.add('is-arriving');
        clearTimeout(headerArriveTimer);
        headerArriveTimer = setTimeout(function () {
          siteHeader.classList.remove('is-arriving');
        }, 400);
      }
    };

    var exitCompact = function () {
      if (!headerIsCompact) return;
      headerIsCompact = false;
      var restore = function () {
        siteHeader.classList.remove('is-compact', 'is-leaving');
        headerSpacer.style.height = '';
        headerExitTimer = null;
      };
      if (!allowMotion) {
        restore();
        return;
      }
      siteHeader.classList.add('is-leaving');
      headerExitTimer = setTimeout(restore, HEADER_EXIT);
    };

    var updateHeader = function (event) {
      var y = window.scrollY;
      /* Landing mid-page (a refresh, or coming back to a restored position)
         should find the bar already there rather than watch it drop in. */
      if (!headerIsCompact && y > headerTop + headerFullHeight) enterCompact(!!event);
      else if (headerIsCompact && y <= headerTop) exitCompact();
    };

    measureHeader();
    updateHeader();
    /* Cheap enough to run raw: the offsets are cached, so a scroll tick is one
       comparison, and the class lands in the same frame as the scroll. */
    window.addEventListener('scroll', updateHeader, { passive: true });
    window.addEventListener('resize', function () {
      measureHeader();
      updateHeader();
    });
  }

  /* Mobile menu toggle */
  initSection(function (scope) {
    scope.querySelectorAll('[data-menu-toggle]').forEach(function (toggle) {
      if (!initOnce(toggle, 'menu-toggle')) return;
      toggle.addEventListener('click', function () {
        var nav = document.querySelector('[data-header-nav]');
        if (!nav) return;
        var open = nav.classList.toggle('is-open');
        toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (!mobileMenuIsModal()) return;
        if (open) menuTrap.activate(toggle);
        else menuTrap.deactivate();
      });
    });
  });

  /* Mobile submenu accordions: the chevron reveals a parent item's nested
     mega-menu links inline (desktop keeps the hover mega menu). */
  initSection(function (scope) {
    scope.querySelectorAll('[data-submenu-toggle]').forEach(function (toggle) {
      if (!initOnce(toggle, 'submenu-toggle')) return;
      toggle.addEventListener('click', function () {
        var item = toggle.closest('.site-header__nav-item');
        if (!item) return;
        var expanded = item.classList.toggle('is-expanded');
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    });
  });

  /* Desktop mega menu hover intent: the panel hangs below the header, so the
     pointer briefly leaves the nav item on its way down. A short close delay
     (plus the CSS bridge band) keeps the menu up during that trip, and keeps it
     from flickering when the pointer clips a corner. */
  initSection(function (megaScope) {
  var megaItems = megaScope.querySelectorAll('.site-header__nav-item.has-mega');
  if (megaItems.length && initOnce(megaItems[0], 'mega')) {
    var MEGA_CLOSE_DELAY = 220;
    var megaTimer = null;
    var desktopMega = window.matchMedia('(min-width: 990px)');

    var closeAllMega = function () {
      megaItems.forEach(function (item) {
        item.classList.remove('is-open');
      });
    };

    var cancelMegaClose = function () {
      if (megaTimer) {
        clearTimeout(megaTimer);
        megaTimer = null;
      }
    };

    megaItems.forEach(function (item) {
      item.addEventListener('pointerenter', function (event) {
        if (event.pointerType === 'touch' || !desktopMega.matches) return;
        cancelMegaClose();
        closeAllMega();
        item.classList.add('is-open');
      });

      item.addEventListener('pointerleave', function (event) {
        if (event.pointerType === 'touch' || !desktopMega.matches) return;
        cancelMegaClose();
        megaTimer = setTimeout(function () {
          item.classList.remove('is-open');
          megaTimer = null;
        }, MEGA_CLOSE_DELAY);
      });

      /* Following a link should not leave the panel hanging over the new page. */
      item.addEventListener('click', function (event) {
        if (event.target.closest('a')) {
          cancelMegaClose();
          closeAllMega();
        }
      });
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') {
        cancelMegaClose();
        closeAllMega();
      }
    });
  }
  });

  /* Header search overlay. The Escape handler is bound once on document and looks
     the elements up lazily, so it keeps working after the header is re-rendered
     instead of closing over stale nodes.

     createFocusTrap is a hoisted function declaration further down the file, so it
     is callable here. Both of these cover the page like the cart drawer does, so
     they get the same treatment. */
  var searchTrap = createFocusTrap(function () {
    var el = document.querySelector('[data-search-overlay]');
    return el && el.querySelector('.search-overlay__panel');
  });

  var menuTrap = createFocusTrap(function () {
    return document.querySelector('[data-header-nav]');
  });

  /* The nav is a plain inline menu on desktop — only trap it when it is actually
     behaving as an overlay. */
  function mobileMenuIsModal() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  function closeSearchOverlay() {
    var toggle = document.querySelector('[data-search-toggle]');
    var overlay = document.querySelector('[data-search-overlay]');
    if (!overlay || overlay.hidden) return;
    overlay.hidden = true;
    if (toggle) toggle.setAttribute('aria-expanded', 'false');
    searchTrap.deactivate();
  }

  initSection(function (scope) {
    var searchToggle = scope.querySelector('[data-search-toggle]');
    var searchOverlay = scope.querySelector('[data-search-overlay]');
    if (!searchToggle || !searchOverlay || !initOnce(searchToggle, 'search-toggle')) return;
    searchToggle.addEventListener('click', function () {
      var open = searchOverlay.hidden;
      if (!open) { closeSearchOverlay(); return; }
      searchOverlay.hidden = false;
      searchToggle.setAttribute('aria-expanded', 'true');
      searchTrap.activate(searchToggle);
      var input = searchOverlay.querySelector('input[type="search"]');
      if (input) input.focus();
    });
  });

  /* Visible close control, plus backdrop dismiss — tapping outside the panel closes,
     as a touch shopper expects. */
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-search-close]')) {
      closeSearchOverlay();
      return;
    }
    var overlay = event.target.closest('[data-search-overlay]');
    if (!overlay || overlay.hidden) return;
    if (event.target.closest('.search-overlay__panel')) return;
    closeSearchOverlay();
  });

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape') return;
    /* deactivate() restores focus to the toggle for us. */
    closeSearchOverlay();
  });

  /* Predictive (instant) search: fetch the predictive-search section as the
     shopper types and render it into the overlay. The GET form stays the no-JS
     fallback and still submits to the full search results page. */
  initSection(function (predScope) {
  var predictiveRoot = predScope.querySelector('[data-predictive]');
  if (predictiveRoot && initOnce(predictiveRoot, 'predictive')) {
    var predInput = predictiveRoot.querySelector('[data-predictive-input]');
    var predResults = predictiveRoot.querySelector('[data-predictive-results]');
    var predUrl = predictiveRoot.getAttribute('data-predictive-url') || '/search/suggest';
    var predSeq = 0;
    var predTimer;

    var closePredictive = function () {
      if (!predResults) return;
      clearTimeout(predTimer);
      predSeq += 1; /* invalidate any pending debounce or in-flight fetch */
      predResults.hidden = true;
      predResults.innerHTML = '';
      predictiveRoot.classList.remove('is-loading');
      if (predInput) predInput.setAttribute('aria-expanded', 'false');
    };

    var runPredictive = function (term) {
      var seq = ++predSeq;
      predictiveRoot.classList.add('is-loading');
      var params = 'q=' + encodeURIComponent(term) +
        '&section_id=predictive-search' +
        '&resources[type]=product,collection,query,page' +
        '&resources[limit]=6';
      fetch(predUrl + (predUrl.indexOf('?') === -1 ? '?' : '&') + params, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          if (!response.ok) throw new Error('predictive search failed');
          return response.text();
        })
        .then(function (html) {
          if (seq !== predSeq) return;
          predResults.innerHTML = html;
          predictiveRoot.classList.remove('is-loading');
          var hasContent = predResults.textContent.trim().length > 0;
          predResults.hidden = !hasContent;
          if (predInput) predInput.setAttribute('aria-expanded', hasContent ? 'true' : 'false');
        })
        .catch(function () { if (seq === predSeq) closePredictive(); });
    };

    if (predInput && predResults) {
      predInput.addEventListener('input', function () {
        var term = predInput.value.trim();
        clearTimeout(predTimer);
        if (term.length < 2) { closePredictive(); return; }
        predTimer = setTimeout(function () { runPredictive(term); }, 220);
      });

      /* ArrowDown from the field moves focus into the results list */
      predInput.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowDown' && !predResults.hidden) {
          var first = predResults.querySelector('a');
          if (first) { event.preventDefault(); first.focus(); }
        }
      });

      /* Arrow keys roam the results; ArrowUp past the top returns to the field */
      predResults.addEventListener('keydown', function (event) {
        if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
        var links = Array.prototype.slice.call(predResults.querySelectorAll('a'));
        if (!links.length) return;
        var index = links.indexOf(document.activeElement);
        event.preventDefault();
        if (event.key === 'ArrowDown') {
          if (index < links.length - 1) links[index + 1].focus();
        } else if (index <= 0) {
          predInput.focus();
        } else {
          links[index - 1].focus();
        }
      });

      /* Clear results when the overlay is dismissed (Escape or the toggle) */
      document.addEventListener('keydown', function (event) {
        if (event.key === 'Escape') closePredictive();
      });
      /* Delegated and looked up lazily: the search toggle lives in the header
         section, which the editor can replace independently of this one. */
      document.addEventListener('click', function (event) {
        if (!event.target.closest('[data-search-toggle]')) return;
        var overlay = document.querySelector('[data-search-overlay]');
        if (overlay && overlay.hidden) closePredictive();
      });
    }
  }
  });

  /* Announcement bar: rotate one message at a time on small screens. The bar used
     flex-wrap, so two default-length messages became two stacked rows at 375px.
     Desktop is untouched — every slide stays inline there. */
  initSection(function (scope) {
    scope.querySelectorAll('[data-announcement]').forEach(function (bar) {
      if (!initOnce(bar, 'announcement')) return;
      var slides = Array.prototype.slice.call(bar.querySelectorAll('[data-announcement-slide]'));
      if (slides.length < 2) return;

      var small = window.matchMedia('(max-width: 989px)');
      var index = 0;
      var timer = null;

      function show(n) {
        index = (n + slides.length) % slides.length;
        slides.forEach(function (slide, i) {
          slide.classList.toggle('is-active', i === index);
        });
      }

      function stop() {
        if (timer) { clearInterval(timer); timer = null; }
      }

      function start() {
        /* Reduced motion: collapse to the first message rather than cycling. */
        if (timer || !small.matches || !allowMotion) return;
        timer = setInterval(function () { show(index + 1); }, 5000);
      }

      function sync() {
        if (small.matches) {
          bar.classList.add('is-rotating');
          show(index);
          start();
        } else {
          stop();
          bar.classList.remove('is-rotating');
          /* Desktop shows them all, so leave no slide hidden behind is-active. */
          slides.forEach(function (slide) { slide.classList.add('is-active'); });
        }
      }

      bar.addEventListener('mouseenter', stop);
      bar.addEventListener('mouseleave', start);
      bar.addEventListener('focusin', stop);
      bar.addEventListener('focusout', start);

      var onResize = function () { sync(); };
      window.addEventListener('resize', onResize);

      /* Editor: selecting a message shows it and holds it there. */
      onBlockSelect(bar, function (block) {
        var n = slides.indexOf(block);
        if (n === -1) return;
        stop();
        if (small.matches) show(n);
      }, start);

      onTeardown(bar, function () {
        stop();
        window.removeEventListener('resize', onResize);
      });

      sync();
    });
  });

  /* Hero slideshow */
  initSection(function (scope) {
  scope.querySelectorAll('[data-hero]').forEach(function (hero) {
    if (!initOnce(hero, 'hero')) return;
    var slides = hero.querySelectorAll('.hero__slide');
    var dots = hero.querySelectorAll('.hero__dot');
    var card = hero.querySelector('.hero__card');
    if (slides.length < 2) return;
    var index = 0;

    /* Dots and arrows sit outside the slides, so they'd keep the first slide's
       contrast forever — mirror the active slide's onto the card instead. */
    function syncTextColor() {
      if (!card || !slides[index]) return;
      card.setAttribute('data-hero-text', slides[index].getAttribute('data-hero-text') || 'light');
    }

    function show(i) {
      index = (i + slides.length) % slides.length;
      slides.forEach(function (slide, n) {
        slide.classList.toggle('is-active', n === index);
      });
      dots.forEach(function (dot, n) {
        dot.classList.toggle('is-active', n === index);
      });
      syncTextColor();
    }

    syncTextColor();

    var autoplay = null;
    var canAutoplay = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    function startAutoplay() {
      if (!canAutoplay || autoplay) return;
      autoplay = setInterval(function () { show(index + 1); }, 6000);
    }
    function stopAutoplay() {
      if (autoplay) { clearInterval(autoplay); autoplay = null; }
    }
    function restartAutoplay() { stopAutoplay(); startAutoplay(); }

    hero.querySelectorAll('[data-hero-prev]').forEach(function (btn) {
      btn.addEventListener('click', function () { show(index - 1); restartAutoplay(); });
    });
    hero.querySelectorAll('[data-hero-next]').forEach(function (btn) {
      btn.addEventListener('click', function () { show(index + 1); restartAutoplay(); });
    });
    dots.forEach(function (dot, n) {
      dot.addEventListener('click', function () { show(n); restartAutoplay(); });
    });

    /* Pause on hover/focus so the slide can be read; pause when the tab is hidden. */
    hero.addEventListener('mouseenter', stopAutoplay);
    hero.addEventListener('mouseleave', startAutoplay);
    hero.addEventListener('focusin', stopAutoplay);
    hero.addEventListener('focusout', startAutoplay);
    /* Bound on document, so it has to be removed explicitly — otherwise editing
       the hero repeatedly stacks up listeners driving dead intervals. */
    var onVisibility = function () {
      if (document.hidden) { stopAutoplay(); } else { startAutoplay(); }
    };
    document.addEventListener('visibilitychange', onVisibility);

    onTeardown(hero, function () {
      stopAutoplay();
      document.removeEventListener('visibilitychange', onVisibility);
    });

    /* Editor: show the slide being edited and hold it there while it's selected. */
    onBlockSelect(hero, function (block) {
      var n = Array.prototype.indexOf.call(slides, block);
      if (n === -1) return;
      stopAutoplay();
      show(n);
    }, function () {
      startAutoplay();
    });

    startAutoplay();
  });
  });

  /* Press logo marquee — Liquid ships two passes, which covers the loop up to
     one pass width. On wider screens that leaves a gap at the end of the cycle,
     so clone whole passes until the track is wider than the viewport and tell
     the animation how far one pass is (translate = 100% / passes). */
  initSection(function (scope) {
  scope.querySelectorAll('[data-press-marquee]').forEach(function (strip) {
    if (!initOnce(strip, 'marquee')) return;
    var track = strip.querySelector('.press-logos__track');
    var passSize = parseInt(strip.getAttribute('data-press-pass-size'), 10);
    if (!track || !allowMotion || !passSize || track.children.length < passSize * 2) return;

    /* The second pass is already aria-hidden and carries no editor attributes */
    var unit = Array.prototype.slice.call(track.children, passSize, passSize * 2);

    function fill() {
      var passWidth = unit.reduce(function (total, item) {
        return total + item.getBoundingClientRect().width;
      }, 0);
      if (!passWidth) return;

      /* One pass scrolls off while the rest covers the strip, so we need the
         visible width covered plus the pass that is leaving. */
      var visible = strip.clientWidth || window.innerWidth;
      var needed = Math.ceil(visible / passWidth) + 1;
      var passes = Math.round(track.children.length / passSize);
      while (passes < needed) {
        var fragment = document.createDocumentFragment();
        unit.forEach(function (item) { fragment.appendChild(item.cloneNode(true)); });
        track.appendChild(fragment);
        passes += 1;
      }
      strip.style.setProperty('--press-passes', passes);
    }

    fill();
    /* Late-decoding logos can change the pass width; recheck on load + resize.
       Both are window-scoped, so they are released when the section goes away. */
    var resizeTimer;
    var onResize = function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(fill, 150);
    };
    window.addEventListener('load', fill);
    window.addEventListener('resize', onResize);
    onTeardown(strip, function () {
      clearTimeout(resizeTimer);
      window.removeEventListener('load', fill);
      window.removeEventListener('resize', onResize);
    });
  });
  });

  /* Pouch showcase tabs (WAI-ARIA tab pattern: roving tabindex + arrow keys) */
  initSection(function (scope) {
  scope.querySelectorAll('[data-pouch-tabs]').forEach(function (root) {
    if (!initOnce(root, 'pouch-tabs')) return;
    var tabs = Array.prototype.slice.call(root.querySelectorAll('.pouch-showcase__tab'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('.pouch-showcase__panel'));
    if (!tabs.length) return;

    function select(n, moveFocus) {
      tabs.forEach(function (t, i) {
        var active = i === n;
        t.classList.toggle('is-active', active);
        t.setAttribute('aria-selected', active ? 'true' : 'false');
        t.setAttribute('tabindex', active ? '0' : '-1');
      });
      panels.forEach(function (p, i) { p.classList.toggle('is-active', i === n); });
      if (moveFocus && tabs[n]) tabs[n].focus();
    }

    tabs.forEach(function (tab, n) {
      tab.addEventListener('click', function () { select(n, false); });
      tab.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (n + 1) % tabs.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (n - 1 + tabs.length) % tabs.length;
        else if (event.key === 'Home') next = 0;
        else if (event.key === 'End') next = tabs.length - 1;
        if (next !== null) { event.preventDefault(); select(next, true); }
      });
    });

    /* Editor: the block attributes sit on the tab button, so selecting a pouch in
       the sidebar switches to its panel. */
    onBlockSelect(root, function (block) {
      var n = tabs.indexOf(block);
      if (n !== -1) select(n, false);
    });
  });
  });

  /* Country/region switcher — submit on change, so the button is only a no-JS fallback */
  initSection(function (scope) {
  scope.querySelectorAll('[data-localization-form]').forEach(function (form) {
    if (!initOnce(form, 'localization')) return;
    var submit = form.querySelector('[data-localization-submit]');
    var select = form.querySelector('[data-localization-select]');
    if (!select) return;
    if (submit) submit.hidden = true;
    select.addEventListener('change', function () {
      // Don't disable the select — a disabled control is omitted from the
      // submission, which would drop country_code entirely.
      select.setAttribute('aria-busy', 'true');
      form.submit();
    });
  });
  });

  /* Quantity steppers */
  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-quantity-change]');
    if (!button) return;
    var wrapper = button.closest('.quantity');
    var input = wrapper && wrapper.querySelector('.quantity__input');
    if (!input) return;
    var step = button.getAttribute('data-quantity-change') === 'up' ? 1 : -1;
    var next = Math.max(parseInt(input.min || '0', 10), (parseInt(input.value, 10) || 0) + step);
    input.value = next;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  /* Cart page: apply quantity changes over AJAX and re-render just this section.
     This used to call form.submit() on every change — a full page reload per keystroke
     committed, losing scroll position each time. It also bound once at load via
     querySelectorAll, so it did not survive a re-render; the listener below is delegated
     on document and does. The no-JS path stays intact: [data-cart-update] posts the form
     natively, and is hidden here only because JS is available. */
  document.querySelectorAll('[data-cart-update]').forEach(function (button) {
    button.hidden = true;
  });

  var cartPageBusy = false;

  function showCartPageError(message) {
    var box = document.querySelector('[data-cart-page-error]');
    if (!box) return;
    box.textContent = message || 'Sorry, that quantity is not available.';
    box.hidden = false;
  }

  function refreshCartSection() {
    var current = document.querySelector('[data-cart-section]');
    if (!current) return Promise.resolve();
    var sectionId = current.getAttribute('data-section-id');
    return fetch(window.location.pathname + '?section_id=' + encodeURIComponent(sectionId))
      .then(function (response) {
        if (!response.ok) throw new Error('cart section fetch failed');
        return response.text();
      })
      .then(function (html) {
        var parser = document.createElement('div');
        parser.innerHTML = html;
        var fresh = parser.querySelector('[data-cart-section]');
        var live = document.querySelector('[data-cart-section]');
        if (fresh && live) {
          live.replaceWith(fresh);
          fresh.querySelectorAll('[data-cart-update]').forEach(function (button) {
            button.hidden = true;
          });
        }
      });
  }

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('[data-cart-form] .quantity__input')) return;
    var section = input.closest('[data-cart-section]');
    if (!section || cartPageBusy) return;
    /* Quantities are keyed by line item key — updates[<key>] — so read the key rather
       than a positional line index, matching how cart-line-row.liquid names the input. */
    var key = (input.getAttribute('name') || '').replace(/^updates\[/, '').replace(/\]$/, '');
    if (!key) return;

    cartPageBusy = true;
    section.setAttribute('aria-busy', 'true');
    var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    fetch(cartRoot + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: Math.max(0, parseInt(input.value, 10) || 0) })
    })
      .then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      })
      .then(function (result) {
        /* Re-render either way — on a rejection the server state is authoritative. */
        return refreshCartSection().then(function () {
          if (!result.ok) {
            showCartPageError(result.data && (result.data.description || result.data.message));
          }
          /* Keep the header badge and drawer consistent with the page. */
          return refreshCartDrawer(false);
        });
      })
      .then(function () {
        cartPageBusy = false;
      })
      .catch(function () {
        cartPageBusy = false;
        /* Fall back to the native form post so the change is never silently lost. */
        var form = input.closest('form');
        if (form) form.submit();
      });
  });

  /* -------------------------------------------------------------------------
     Cart drawer: open/close, AJAX add-to-cart, section re-render on changes */
  var drawer = document.querySelector('[data-cart-drawer]');
  var drawerBusy = false;

  /* The drawer is its own section, so the editor can replace it. The click and
     keydown handlers below are delegated on document and survive; this only
     re-points the cached element they operate on. */
  initSection(function (scope) {
    var freshDrawer = scope.querySelector('[data-cart-drawer]');
    if (freshDrawer) drawer = freshDrawer;
  });

  function refreshCartDrawer(open) {
    /* Append `sections` to the existing query rather than replacing it. Building the URL
       from pathname alone dropped every other param, so re-rendering from a filtered
       collection or a ?variant= PDP asked the server to render the section without the
       context the page was actually showing. */
    var drawerUrl = new URL(window.location.href);
    drawerUrl.searchParams.set('sections', 'cart-drawer');
    return fetch(drawerUrl.pathname + drawerUrl.search)
      .then(function (response) { return response.json(); })
      .then(function (sections) {
        var html = document.createElement('div');
        html.innerHTML = sections['cart-drawer'];
        var fresh = html.querySelector('[data-cart-drawer]');
        var current = document.querySelector('[data-cart-drawer]');
        if (fresh && current) {
          var wasOpen = current.classList.contains('is-open');
          current.replaceWith(fresh);
          drawer = fresh;
          if (wasOpen || open) openDrawer();
          var count = parseInt(fresh.getAttribute('data-item-count'), 10) || 0;
          document.querySelectorAll('[data-cart-count]').forEach(function (badge) {
            var changed = badge.textContent !== String(count);
            badge.textContent = count;
            badge.hidden = count === 0;
            if (changed && count > 0 && allowMotion) {
              badge.classList.remove('is-bump');
              void badge.offsetWidth;
              badge.classList.add('is-bump');
            }
          });
          startReservationTimer();
        }
      });
  }

  /* ---------------------------------------------------------------------------
     Modal focus management, shared by the cart drawer, the search overlay and the
     mobile menu. The drawer declared role="dialog" aria-modal="true" and honoured
     neither: Tab walked straight out into the page behind it, and closing dropped
     focus on <body>, so a keyboard or screen-reader shopper could not reliably get
     back to where they were — or check out.

     getRoot is a function, not an element, because refreshCartDrawer replaces the
     drawer wholesale; the trap has to look the panel up fresh every time. */
  var FOCUSABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]),' +
    ' select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  function focusableWithin(root) {
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE_SELECTOR), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0;
    });
  }

  /* Mark everything except the dialog's own top-level ancestor as inert, so the
     background is unreachable by Tab, by screen readers and by pointer. */
  function setBackgroundInert(root, on) {
    if (!root) return;
    var top = root;
    while (top.parentElement && top.parentElement !== document.body) top = top.parentElement;
    Array.prototype.forEach.call(document.body.children, function (child) {
      if (child === top || child.tagName === 'SCRIPT') return;
      if (on) {
        child.setAttribute('inert', '');
        child.setAttribute('aria-hidden', 'true');
      } else {
        child.removeAttribute('inert');
        child.removeAttribute('aria-hidden');
      }
    });
  }

  function createFocusTrap(getRoot) {
    var lastFocused = null;
    var active = false;

    document.addEventListener('keydown', function (event) {
      if (!active || event.key !== 'Tab') return;
      var root = getRoot();
      if (!root) return;
      var items = focusableWithin(root);
      if (!items.length) return;
      var first = items[0];
      var last = items[items.length - 1];
      var inside = root.contains(document.activeElement);
      if (event.shiftKey && (document.activeElement === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    });

    return {
      isActive: function () { return active; },
      activate: function (trigger) {
        var root = getRoot();
        if (!root) return;
        /* Re-entrant: refreshCartDrawer re-opens an already-open drawer after every
           cart change. That must not overwrite the element we owe focus back to,
           and must not yank focus to the top of the panel mid-interaction — so we
           only move focus on a genuine open, or if the re-render orphaned it. */
        var wasActive = active;
        if (!active) lastFocused = trigger || document.activeElement;
        active = true;
        setBackgroundInert(root, true);
        if (!wasActive || !root.contains(document.activeElement)) {
          var items = focusableWithin(root);
          if (items.length) items[0].focus();
        }
      },
      /* The panel was replaced underneath us while open — put focus back inside. */
      refocus: function () {
        if (!active) return;
        var root = getRoot();
        if (!root) return;
        setBackgroundInert(root, true);
        if (!root.contains(document.activeElement)) {
          var items = focusableWithin(root);
          if (items.length) items[0].focus();
        }
      },
      deactivate: function () {
        if (!active) return;
        active = false;
        setBackgroundInert(getRoot(), false);
        if (lastFocused && document.contains(lastFocused)) lastFocused.focus();
        lastFocused = null;
      }
    };
  }

  var drawerTrap = createFocusTrap(function () {
    var el = document.querySelector('[data-cart-drawer]');
    return el && el.querySelector('.cart-drawer__panel');
  });

  function openDrawer(trigger) {
    if (!drawer) return;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    drawerTrap.activate(trigger);
  }

  function closeDrawer() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    drawerTrap.deactivate();
  }

  /* The drawer's equivalent of showCartError. That one appends into the product form;
     the drawer has no form, and refreshCartDrawer swaps the whole element, so the box is
     (re)created against the live panel each time and must be written AFTER a refresh. */
  function showDrawerError(message) {
    var panel = document.querySelector('[data-cart-drawer] .cart-drawer__panel');
    if (!panel) return;
    var box = panel.querySelector('[data-drawer-error]');
    if (!box) {
      box = document.createElement('p');
      box.className = 'cart-error cart-drawer__error';
      box.setAttribute('data-drawer-error', '');
      box.setAttribute('role', 'alert');
      var header = panel.querySelector('.cart-drawer__header');
      if (header) header.insertAdjacentElement('afterend', box);
      else panel.insertBefore(box, panel.firstChild);
    }
    box.textContent = message || 'Sorry, that quantity is not available.';
    box.hidden = false;
  }

  function clearDrawerError() {
    var box = document.querySelector('[data-cart-drawer] [data-drawer-error]');
    if (box) box.hidden = true;
  }

  if (drawer) {
    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-cart-drawer-open]');
      if (opener) {
        event.preventDefault();
        /* Pass the trigger explicitly so focus returns to the cart button on close. */
        openDrawer(opener);
        return;
      }
      if (event.target.closest('[data-cart-drawer-close]')) {
        closeDrawer();
        return;
      }
      var qtyButton = event.target.closest('[data-drawer-qty-change]');
      if (qtyButton) {
        if (drawerBusy) return;
        drawerBusy = true;
        drawer.classList.add('is-busy');
        var line = qtyButton.getAttribute('data-line');
        var quantity = parseInt(qtyButton.getAttribute('data-drawer-qty-change'), 10);
        fetch(window.Shopify && window.Shopify.routes ? window.Shopify.routes.root + 'cart/change.js' : '/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: parseInt(line, 10), quantity: Math.max(0, quantity) })
        }).then(function (response) {
          /* Check the status. cart/change.js answers 422 with a real reason — inventory
             ("Only 1 item was added to your cart due to availability"), a MinMaxify order
             limit, and so on. This used to chain straight into the refresh, so the drawer
             silently snapped the quantity back and told the shopper nothing. */
          return response.json().then(function (data) {
            return { ok: response.ok, data: data };
          });
        }).then(function (result) {
          /* Refresh either way — on a rejection the server state is authoritative and the
             stepper must show what the cart really holds. The message goes on afterwards,
             because refreshCartDrawer replaces the drawer element wholesale. */
          return refreshCartDrawer(true).then(function () {
            if (result.ok) {
              clearDrawerError();
            } else {
              showDrawerError(result.data && (result.data.description || result.data.message));
            }
          });
        }).then(function () {
          drawerBusy = false;
        }).catch(function () {
          drawerBusy = false;
          if (drawer) drawer.classList.remove('is-busy');
        });
      }
    });

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && drawer.classList.contains('is-open')) closeDrawer();
    });

    /* AJAX add-to-cart: intercept product forms so adds open the drawer */
    function showCartError(form, message) {
      var box = form.querySelector('[data-cart-error]');
      if (!box) {
        box = document.createElement('p');
        box.className = 'cart-error';
        box.setAttribute('data-cart-error', '');
        box.setAttribute('role', 'alert');
        form.appendChild(box);
      }
      box.textContent = message || 'Sorry, this item could not be added to your cart.';
      box.hidden = false;
    }

    function clearCartError(form) {
      var box = form.querySelector('[data-cart-error]');
      if (box) box.hidden = true;
    }

    function pulseAdded(button) {
      if (!button || !allowMotion) return;
      button.classList.remove('is-added');
      void button.offsetWidth;
      button.classList.add('is-added');
      setTimeout(function () { button.classList.remove('is-added'); }, 600);
    }

    document.addEventListener('submit', function (event) {
      var form = event.target;
      /* Allow-list, not `form[action*="/cart/add"]`. That matched EVERY add-to-cart form
         on the page, including ones injected by Free Gift Cart Upsell and Honeycomb —
         re-routing those apps' own add flows through our drawer refresh and past whatever
         response handling they expect. Unknown third-party forms are now left alone by
         default; only forms the theme renders carry this attribute. */
      if (!form.matches('form[data-theme-add-to-cart]')) return;
      event.preventDefault();
      /* One add in flight per form — a rapid double-click must not POST twice */
      if (form.themeAtcBusy) return;
      form.themeAtcBusy = true;
      var addButton = form.querySelector('[type="submit"]');
      if (addButton) addButton.setAttribute('aria-disabled', 'true');
      var settle = function () {
        form.themeAtcBusy = false;
        if (addButton) addButton.removeAttribute('aria-disabled');
      };
      var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(cartRoot + 'cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      }).then(function (response) {
        return response.json().then(function (data) {
          return { ok: response.ok, data: data };
        });
      }).then(function (result) {
        if (!result.ok) {
          /* Surface the reason (sold out, order limit from MinMaxify, etc.) */
          showCartError(form, result.data && (result.data.description || result.data.message));
          settle();
          return;
        }
        clearCartError(form);
        pulseAdded(addButton);
        try { sessionStorage.setItem('100ml:cart-deadline', String(Date.now() + reservationMs())); } catch (e) { /* ignore */ }
        /* The item is in the cart past this point. A failure below is only the
           drawer re-render, so it must never reach the re-posting catch — show
           the cart page instead of adding the item a second time. */
        return refreshCartDrawer(true).then(settle, function () {
          window.location.href = cartRoot + 'cart';
        });
      }).catch(function () {
        /* Network/parse failure on the add itself — fall back to a full-page form post */
        settle();
        form.submit();
      });
    });
  }

  /* Reservation countdown (display only) */
  var reservationInterval;

  function reservationMs() {
    var el = document.querySelector('[data-reservation-timer]');
    var minutes = el ? parseInt(el.getAttribute('data-minutes'), 10) || 10 : 10;
    return minutes * 60 * 1000;
  }

  /* The countdown is only shown when the shopper genuinely added to cart this
     session (the add-to-cart handlers set '100ml:cart-deadline'). It is never
     fabricated on a cold page load, so it can't imply a reservation that doesn't
     exist. The markup ships hidden; this reveals it only for a live deadline. */
  function startReservationTimer() {
    var el = document.querySelector('[data-reservation-timer]');
    if (!el) return;
    var wrapper = el.closest('[data-reservation]');
    var deadline = 0;
    try {
      deadline = parseInt(sessionStorage.getItem('100ml:cart-deadline'), 10) || 0;
    } catch (error) {
      deadline = 0;
    }
    clearInterval(reservationInterval);
    if (!deadline || deadline <= Date.now()) {
      if (wrapper) wrapper.hidden = true;
      return;
    }
    if (wrapper) wrapper.hidden = false;
    reservationInterval = setInterval(function () {
      var current = document.querySelector('[data-reservation-timer]');
      if (!current) { clearInterval(reservationInterval); return; }
      var remaining = Math.max(0, deadline - Date.now());
      var mins = Math.floor(remaining / 60000);
      var secs = Math.floor((remaining % 60000) / 1000);
      current.textContent = (mins < 10 ? '0' + mins : mins) + ':' + (secs < 10 ? '0' + secs : secs);
      if (remaining === 0) {
        clearInterval(reservationInterval);
        if (wrapper) wrapper.hidden = true;
      }
    }, 1000);
  }

  startReservationTimer();

  /* Cart page: persist the order note via AJAX so saving doesn't reload the page.
     The note is also part of the cart form, so checkout captures the latest value
     even if the field was never blurred. */
  document.querySelectorAll('[data-cart-form] [data-cart-note]').forEach(function (note) {
    note.addEventListener('change', function () {
      var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(cartRoot + 'cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: note.value })
      });
    });
  });

  /* Reviews: "Write a review" opens the Judge.me form (or scrolls to the widget) */
  document.querySelectorAll('[data-write-review]').forEach(function (button) {
    button.addEventListener('click', function () {
      var jdgmLink = document.querySelector('.jdgm-write-rev-link');
      if (jdgmLink) {
        jdgmLink.click();
      } else {
        var widget = document.querySelector('.jdgm-rev-widg');
        if (widget) widget.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  /* Newsletter: subscribe via Klaviyo when configured, else let the native
     Shopify customer form submit. Falls back to native submit on any failure. */
  var klaviyo = (window.theme && window.theme.klaviyo) || {};
  if (klaviyo.key && klaviyo.list) {
    document.querySelectorAll('.newsletter__form').forEach(function (form) {
      form.addEventListener('submit', function (event) {
        var emailInput = form.querySelector('input[type="email"]');
        if (!emailInput || !emailInput.checkValidity()) return; // let native validation run
        event.preventDefault();
        var message = form.querySelector('[data-newsletter-message]');
        function showMessage(text) {
          if (message) { message.textContent = text; message.hidden = false; }
        }
        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(klaviyo.key), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: { profile: { data: { type: 'profile', attributes: { email: emailInput.value } } } },
              relationships: { list: { data: { type: 'list', id: klaviyo.list } } }
            }
          })
        }).then(function (response) {
          if (response.ok || response.status === 202) {
            showMessage('Thanks for subscribing — see you at the gate.');
            emailInput.value = '';
          } else {
            form.submit(); // fall back to native capture
          }
        }).catch(function () {
          form.submit();
        });
      });
    });
  }

  /* Swap the gallery's main image. `image_tag` renders a srcset, and a srcset
     always wins over src — so rewrite both, rebuilding the candidates off the
     new CDN url's width param. */
  var GALLERY_WIDTHS = [352, 832, 1200, 1400];

  function setGalleryImage(img, src, alt) {
    if (!img || !src) return;
    img.src = src;
    if (/[?&]width=\d+/.test(src)) {
      img.srcset = GALLERY_WIDTHS.map(function (width) {
        return src.replace(/([?&]width=)\d+/, '$1' + width) + ' ' + width + 'w';
      }).join(', ');
    } else {
      img.removeAttribute('srcset');
    }
    if (alt) img.alt = alt;
    /* Keep the zoom source pointing at the image now on screen, so zooming
       after a variant or thumbnail swap doesn't reopen the original shot. */
    var zoomTarget = img.closest('[data-gallery-zoom]');
    if (zoomTarget) {
      zoomTarget.setAttribute(
        'data-zoom-src',
        /[?&]width=\d+/.test(src) ? src.replace(/([?&]width=)\d+/, '$12000') : src
      );
    }
  }

  /* Product gallery thumbnails */
  initSection(function (scope) {
  scope.querySelectorAll('[data-product-gallery]').forEach(function (gallery) {
    if (!initOnce(gallery, 'gallery')) return;
    var main = gallery.querySelector('[data-gallery-main] img');
    var mainButton = gallery.querySelector('[data-gallery-main]');
    /* Queried per click rather than captured once: querySelectorAll returns a static
       NodeList, so a panel added after init (a re-render, or the editor) would be
       invisible to a cached copy. */
    function mediaPanels() { return gallery.querySelectorAll('[data-gallery-media]'); }

    /* Pause any playing video before swapping away from it. */
    function stopMedia(panel) {
      var video = panel.querySelector('video');
      if (video && typeof video.pause === 'function') video.pause();
    }

    gallery.querySelectorAll('.product-gallery__thumb').forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        gallery.querySelectorAll('.product-gallery__thumb').forEach(function (t) {
          t.classList.toggle('is-active', t === thumb);
        });

        var type = thumb.getAttribute('data-media-type') || 'image';
        var mediaId = thumb.getAttribute('data-media-id');

        if (type === 'image') {
          mediaPanels().forEach(function (panel) { stopMedia(panel); panel.hidden = true; });
          if (mainButton) mainButton.hidden = false;
          if (!main) return;
          var src = thumb.getAttribute('data-full-src');
          if (!src) return;
          var thumbImage = thumb.querySelector('img');
          setGalleryImage(main, src, thumbImage && thumbImage.alt);
          return;
        }

        /* Video / external video / 3D model: swap the still image out for the player. */
        if (mainButton) mainButton.hidden = true;
        mediaPanels().forEach(function (panel) {
          var match = panel.getAttribute('data-gallery-media') === mediaId;
          if (!match) stopMedia(panel);
          panel.hidden = !match;
        });
      });
    });
  });
  });

  /* Product image zoom. The live store ships a cursor-zoom-in affordance and the
     redesign had none, so a shopper couldn't inspect a 100mL bottle's label.
     Built as a dialog-ish overlay rather than a library: open on click or
     keyboard, Esc/backdrop/button to close, focus trapped while open and
     restored to the trigger on close. */
  initSection(function productZoom(scope) {
    var trigger = scope.querySelector('[data-gallery-zoom]');
    if (!trigger || !initOnce(trigger, 'zoom')) return;

    var overlay = null;
    var lastFocused = null;

    function close() {
      if (!overlay) return;
      overlay.remove();
      overlay = null;
      document.body.classList.remove('has-zoom-open');
      document.removeEventListener('keydown', onKeydown);
      /* Return focus to whatever opened it. A mouse click doesn't necessarily
         focus the button, so fall back to the trigger rather than dropping
         focus to <body> and stranding keyboard users at the top of the page. */
      var restoreTo = lastFocused && lastFocused !== document.body && document.contains(lastFocused)
        ? lastFocused
        : trigger;
      if (restoreTo) restoreTo.focus();
    }

    function onKeydown(event) {
      if (!overlay) return;
      if (event.key === 'Escape') {
        close();
        return;
      }
      if (event.key === 'Tab') {
        /* Only the close button is focusable inside, so keep Tab on it. */
        var closeBtn = overlay.querySelector('[data-zoom-close]');
        event.preventDefault();
        if (closeBtn) closeBtn.focus();
      }
    }

    function open() {
      var src = trigger.getAttribute('data-zoom-src');
      var current = trigger.querySelector('img');
      if (!src && current) src = current.src;
      if (!src) return;

      lastFocused = document.activeElement;

      overlay = document.createElement('div');
      overlay.className = 'zoom-overlay';
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.setAttribute('aria-label', trigger.getAttribute('data-zoom-label') || 'Product image');

      var closeBtn = document.createElement('button');
      closeBtn.type = 'button';
      closeBtn.className = 'zoom-overlay__close';
      closeBtn.setAttribute('data-zoom-close', '');
      closeBtn.setAttribute('aria-label', trigger.getAttribute('data-zoom-close-label') || 'Close');
      closeBtn.innerHTML = '&times;';

      var img = document.createElement('img');
      img.className = 'zoom-overlay__image';
      img.src = src;
      img.alt = current ? current.alt : '';

      overlay.appendChild(closeBtn);
      overlay.appendChild(img);
      document.body.appendChild(overlay);
      document.body.classList.add('has-zoom-open');

      closeBtn.addEventListener('click', close);
      overlay.addEventListener('click', function (event) {
        /* Backdrop only — clicking the image itself shouldn't dismiss it. */
        if (event.target === overlay) close();
      });
      document.addEventListener('keydown', onKeydown);
      closeBtn.focus();
    }

    trigger.addEventListener('click', open);
  });

  /* Sticky add-to-cart bar: show once the buy box scrolls out of view */
  initSection(function (scope) {
    var stickyBar = scope.querySelector('[data-sticky-atc]');
    var buyBox = scope.querySelector('[data-buy-box]');
    if (!stickyBar || !buyBox || !('IntersectionObserver' in window)) return;
    if (!initOnce(buyBox, 'sticky-atc')) return;
    var stickyObserver = new IntersectionObserver(function (entries) {
      var show = !entries[0].isIntersecting;
      stickyBar.classList.toggle('is-visible', show);
      /* Keep the ARIA state and the class in step: the bar ships aria-hidden and
         its controls must not be exposed (or tabbable — CSS visibility) while hidden */
      stickyBar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }, { rootMargin: '-80px 0px 0px 0px' });
    stickyObserver.observe(buyBox);
    onTeardown(buyBox, function () { stickyObserver.disconnect(); });
  });

  /* PDP: keep the main qty, the sticky-bar qty, and the button total in sync */
  initSection(function (pdpScope) {
  var priceEl = pdpScope.querySelector('[data-price]');
  var mainQty = pdpScope.querySelector('[data-quantity-input]');
  var mirrorQty = pdpScope.querySelector('[data-quantity-mirror]');
  var atcTotal = pdpScope.querySelector('[data-atc-total]');
  if (priceEl && mainQty && initOnce(priceEl, 'pdp-variants')) {
    function syncQuantity(source) {
      var qty = Math.max(1, parseInt(source.value, 10) || 1);
      var unitPrice = parseInt(priceEl.getAttribute('data-unit-price'), 10);
      if (mainQty.value !== String(qty)) mainQty.value = qty;
      if (mirrorQty && mirrorQty.value !== String(qty)) mirrorQty.value = qty;
      if (atcTotal && unitPrice) {
        atcTotal.innerHTML = '&nbsp;— ' + formatMoney(unitPrice * qty);
      }
    }

    mainQty.addEventListener('change', function () { syncQuantity(mainQty); });
    if (mirrorQty) {
      mirrorQty.addEventListener('change', function () {
        syncQuantity(mirrorQty);
      });
    }

    /* Variant switching: resolve the variant from the option selects, then
       update price, compare-at, availability, sticky bar, URL, and gallery */
    var variantsData = pdpScope.querySelector('[data-variants-json]');
    var optionSelects = pdpScope.querySelectorAll('[data-option-select]');
    if (variantsData && optionSelects.length > 0) {
      var variants = [];
      try {
        variants = JSON.parse(variantsData.textContent);
      } catch (error) {
        variants = [];
      }

      var variantIdInput = pdpScope.querySelector('[data-variant-id]');
      var comparePriceEl = pdpScope.querySelector('[data-compare-price]');
      var atcButton = pdpScope.querySelector('[data-atc-button]');
      var atcLabel = pdpScope.querySelector('[data-atc-label]');
      var stickyPrice = pdpScope.querySelector('[data-sticky-price]');
      var stickyButton = pdpScope.querySelector('[data-sticky-atc-button]');
      var galleryMain = pdpScope.querySelector('[data-gallery-main] img');

      function selectedOptions() {
        var options = [];
        optionSelects.forEach(function (select) {
          var position = parseInt(select.getAttribute('data-option-position'), 10);
          options[position - 1] = select.value;
        });
        return options;
      }

      function findVariant(options) {
        return variants.find(function (variant) {
          return options.every(function (value, index) {
            return variant['option' + (index + 1)] === value;
          });
        });
      }

      function applyVariant(variant) {
        var available = !!(variant && variant.available);
        if (variant) {
          if (variantIdInput) variantIdInput.value = variant.id;
          priceEl.setAttribute('data-unit-price', variant.price);
          priceEl.textContent = formatMoney(variant.price);
          if (comparePriceEl) {
            if (variant.compare_at_price && variant.compare_at_price > variant.price) {
              comparePriceEl.textContent = formatMoney(variant.compare_at_price);
              comparePriceEl.hidden = false;
            } else {
              comparePriceEl.hidden = true;
            }
          }
          if (stickyPrice) stickyPrice.textContent = formatMoney(variant.price);
          if (galleryMain && variant.featured_image && variant.featured_image.src) {
            var src = variant.featured_image.src;
            setGalleryImage(
              galleryMain,
              src + (src.indexOf('?') === -1 ? '?' : '&') + 'width=1400',
              variant.featured_image.alt || variant.name
            );
          }
          if (window.history && window.history.replaceState) {
            var url = new URL(window.location.href);
            url.searchParams.set('variant', variant.id);
            window.history.replaceState({}, '', url);
          }
        } else {
          /* No variant matches the chosen combination. Disabling the button is not
             enough on its own — leaving the previous variant's id in the hidden input
             means a fast double-click, or a submit with JS mid-update, can post a
             STALE variant and sell the shopper the option they had selected a moment
             ago. Clear the id and drop the query param so there is nothing to post. */
          if (variantIdInput) variantIdInput.value = '';
          if (window.history && window.history.replaceState) {
            var staleUrl = new URL(window.location.href);
            staleUrl.searchParams.delete('variant');
            window.history.replaceState({}, '', staleUrl);
          }
        }
        if (atcLabel) {
          atcLabel.textContent = variant ? (available ? 'Add to Cart' : 'Sold out') : 'Unavailable';
        }
        if (atcTotal) atcTotal.hidden = !available;
        if (atcButton) atcButton.disabled = !available;
        if (stickyButton) stickyButton.disabled = !available;
        syncQuantity(mainQty);
      }

      optionSelects.forEach(function (select) {
        select.addEventListener('change', function () {
          applyVariant(findVariant(selectedOptions()));
        });
      });
    }
  }
  });

  /* Collection filtering via the Section Rendering API: facet, sort, price and
     pagination changes re-render just the collection section instead of a full
     page reload, and keep the URL in sync (back/forward supported). The GET form
     and pagination links remain the no-JS fallback. */
  if (document.querySelector('[data-collection-section]')) {
    var collectionSeq = 0;

    /* The filter panel ships `open` so it stays reachable with no JS. Collapse it on
       small viewports, where an open rail pushes the first product card off-screen.
       Desktop keeps it open and CSS hides the summary entirely. */
    var filtersAreCollapsible = function () {
      return window.matchMedia('(max-width: 989px)').matches;
    };

    var applyFilterDisclosure = function (root, forceOpen) {
      var panel = (root || document).querySelector('[data-filters-disclosure]');
      if (!panel) return;
      panel.open = forceOpen == null ? !filtersAreCollapsible() : forceOpen;
    };

    /* Re-applied for any collection section the editor loads: the server always
       sends the panel open, so a reloaded section would otherwise appear expanded
       on mobile. */
    initSection(function (scope) { applyFilterDisclosure(scope); });

    var renderCollection = function (url, opts) {
      opts = opts || {};
      var current = document.querySelector('[data-collection-section]');
      if (!current) { window.location.href = url; return; }
      /* Carry the shopper's open/closed choice across the re-render — the server
         always sends the panel open, so without this it springs open mid-filtering. */
      var openPanel = current.querySelector('[data-filters-disclosure]');
      var wasOpen = openPanel ? openPanel.open : null;
      var seq = ++collectionSeq;
      current.classList.add('is-loading');
      current.setAttribute('aria-busy', 'true');
      var sectionId = current.getAttribute('data-section-id');
      var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(sectionId);
      fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          if (!response.ok) throw new Error('collection fetch failed');
          return response.text();
        })
        .then(function (html) {
          if (seq !== collectionSeq) return; /* a newer request superseded this one */
          var parser = document.createElement('div');
          parser.innerHTML = html;
          var fresh = parser.querySelector('[data-collection-section]');
          if (!fresh) throw new Error('collection section missing');
          current.replaceWith(fresh);
          initPriceRange(fresh);
          applyFilterDisclosure(fresh, wasOpen);
          if (opts.push !== false) {
            try { history.pushState({ collection: true }, '', url); } catch (error) { /* ignore */ }
          }
          if (opts.scroll) {
            var top = fresh.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: top, behavior: allowMotion ? 'smooth' : 'auto' });
          }
        })
        .catch(function () {
          if (seq === collectionSeq) window.location.href = url; /* fall back to a full load */
        });
    };

    var collectionUrl = function () {
      var form = document.querySelector('[data-collection-filters]');
      if (!form) return location.pathname;
      var params = new URLSearchParams(new FormData(form));
      params.delete('page'); /* any facet/sort change resets to page 1 */
      var query = params.toString();
      return location.pathname + (query ? '?' + query : '');
    };

    document.addEventListener('change', function (event) {
      var target = event.target;
      if (!target.closest || !target.closest('[data-collection-section]')) return;
      if (target.matches('[data-collection-filters] input') ||
        target.matches('[data-collection-sort]') ||
        target.matches('[data-price-min]') ||
        target.matches('[data-price-max]')) {
        renderCollection(collectionUrl(), { push: true, scroll: false });
      }
    });

    document.addEventListener('click', function (event) {
      var link = event.target.closest('a');
      if (!link || !link.closest('[data-collection-section]')) return;
      var isPage = !!link.closest('.pagination');
      if (isPage || link.hasAttribute('data-collection-clear')) {
        event.preventDefault();
        renderCollection(link.href, { push: true, scroll: isPage });
      }
    });

    window.addEventListener('popstate', function () {
      if (document.querySelector('[data-collection-section]')) {
        renderCollection(location.href, { push: false, scroll: false });
      }
    });
  }

  /* Login: reveal the password-recover form (CSS :target is the no-JS fallback) */
  document.querySelectorAll('[data-recover-toggle]').forEach(function (toggle) {
    toggle.addEventListener('click', function (event) {
      var target = document.getElementById('RecoverPassword');
      if (target) {
        target.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        var firstField = target.querySelector('input');
        if (firstField) firstField.focus();
        event.preventDefault();
      }
    });
  });

  /* Content pages: build the "on this page" index from the h2s in the body.
     The rich text editor and Shopify's policy bodies cannot emit ids, so they
     are assigned here; the aside stays hidden unless there are at least two
     headings to jump between. */
  (function initPageToc() {
    var toc = document.querySelector('[data-page-toc]');
    var list = document.querySelector('[data-page-toc-list]');
    var body = document.querySelector('[data-page-body]');
    if (!toc || !list || !body) return;

    var headings = body.querySelectorAll('h2');
    if (headings.length < 2) return;

    headings.forEach(function (heading, index) {
      if (!heading.id) {
        var slug = (heading.textContent || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '');
        heading.id = slug ? 'section-' + slug : 'section-' + (index + 1);
      }
      var item = document.createElement('li');
      var link = document.createElement('a');
      link.href = '#' + heading.id;
      link.textContent = heading.textContent;
      item.appendChild(link);
      list.appendChild(item);
    });

    toc.hidden = false;
    var layout = toc.closest('.page-body__layout');
    if (layout) layout.classList.add('page-body__layout--has-toc');
  })();

  /* Destructive account actions confirm before posting (no inline handlers). */
  document.querySelectorAll('[data-confirm-message]').forEach(function (control) {
    control.addEventListener('click', function (event) {
      if (!window.confirm(control.getAttribute('data-confirm-message'))) {
        event.preventDefault();
      }
    });
  });

  /* Collection filters: dual-thumb price slider (visual only). Applying the filter
     on `change` is handled by the AJAX collection module above (or, without JS, by
     submitting the GET form). Re-runnable so it re-binds after a section swap. */
  /* DORMANT BY DESIGN — this is not dead code, and it is not broken.
     The dual-thumb slider renders only for a `price_range` filter, and this store's
     Search & Discovery exposes just filter.p.vendor / filter.p.tag / filter.p.product_type.
     It therefore never appears on the storefront today. Enabling a price filter in
     admin → Search & Discovery → Filters is all it takes to activate it — at which point
     also give price its chip in the active-filter row (see the note in main-collection.liquid). */
  function initPriceRange(root) {
    (root || document).querySelectorAll('[data-price-range]').forEach(function (range) {
      if (range.getAttribute('data-price-init') === '1') return;
      range.setAttribute('data-price-init', '1');
      var minInput = range.querySelector('[data-price-min]');
      var maxInput = range.querySelector('[data-price-max]');
      var active = range.querySelector('[data-price-active]');
      var output = range.parentElement.querySelector('[data-price-output]');
      if (!minInput || !maxInput) return;
      var max = parseInt(range.getAttribute('data-max'), 10) || 100;

      function update() {
        var lo = Math.min(parseInt(minInput.value, 10), parseInt(maxInput.value, 10));
        var hi = Math.max(parseInt(minInput.value, 10), parseInt(maxInput.value, 10));
        if (active) {
          active.style.left = (lo / max) * 100 + '%';
          active.style.width = ((hi - lo) / max) * 100 + '%';
        }
        if (output) output.textContent = '$' + lo + ' – $' + hi;
      }

      [minInput, maxInput].forEach(function (input) {
        input.addEventListener('input', update);
      });
      update();
    });
  }
  /* initPriceRange already takes a root and guards with data-price-init, so it can
     be registered directly. */
  initSection(initPriceRange);

  /* Recently viewed: record on product pages */
  var productData = document.querySelector('[data-product-json]');
  if (productData) {
    try {
      var product = JSON.parse(productData.textContent);
      var key = '100ml:recently-viewed';
      var list = JSON.parse(localStorage.getItem(key) || '[]').filter(function (item) {
        return item.handle !== product.handle;
      });
      list.unshift(product);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 10)));
    } catch (error) {
      /* localStorage unavailable — ignore */
    }
  }

  /* Recently viewed: render the section where present */
  initSection(function (scope) {
  var recentSection = scope.querySelector('[data-recently-viewed]');
  if (recentSection && initOnce(recentSection, 'recently-viewed')) {
    try {
      var recent = JSON.parse(localStorage.getItem('100ml:recently-viewed') || '[]');
      var listEl = recentSection.querySelector('[data-recently-viewed-list]');
      if (recent.length > 0 && listEl) {
        recent.slice(0, 5).forEach(function (item) {
          var card = document.createElement('div');
          card.className = 'product-card';
          var vendor = item.vendor
            ? '<p class="product-card__vendor"></p>'
            : '';
          card.innerHTML =
            '<a class="product-card__media" href=""><img alt="" loading="lazy"></a>' +
            '<div class="product-card__info"><div>' + vendor +
            '<h3 class="product-card__title"><a href=""></a></h3></div>' +
            '<p class="product-card__price"></p></div>';
          card.querySelectorAll('a').forEach(function (a) { a.href = item.url; });
          /* The image link wraps an alt="" image, so without this it is an anchor with
             no accessible name — the Liquid card sets the same label. */
          card.querySelector('.product-card__media').setAttribute('aria-label', item.title);
          var img = card.querySelector('img');
          if (item.image) {
            img.src = item.image;
            /* Same widths the Liquid card uses (product-card.liquid), so a rendition the
               browser already has cached is reused instead of fetching a second size.
               Without this the row shipped one fixed resolution to every device. */
            if (item.image.indexOf('width=') !== -1) {
              img.setAttribute('srcset', ['300', '450', '600'].map(function (w) {
                return item.image.replace(/([?&])width=\d+/, '$1width=' + w) + ' ' + w + 'w';
              }).join(', '));
              img.setAttribute('sizes', '(max-width: 749px) 45vw, 220px');
            }
            /* Intrinsic dimensions come from the stored product JSON rather than being
               assumed square — .product-card__media already reserves the box height in
               CSS, so these only supply the aspect ratio. */
            if (item.image_width && item.image_height) {
              img.setAttribute('width', item.image_width);
              img.setAttribute('height', item.image_height);
            }
          } else {
            img.remove();
          }
          if (item.vendor) card.querySelector('.product-card__vendor').textContent = item.vendor;
          card.querySelector('.product-card__title a').textContent = item.title;
          /* The stored price was formatted in the currency it was viewed in; showing
             it to a session browsing another currency would be plain wrong, so drop
             it there — the card still links to the live PDP. */
          var priceEl = card.querySelector('.product-card__price');
          var activeCurrency = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
          if (item.price_currency && activeCurrency && item.price_currency !== activeCurrency) {
            priceEl.remove();
          } else {
            priceEl.textContent = item.price;
          }
          listEl.appendChild(card);
        });
        recentSection.hidden = false;
      }
    } catch (error) {
      /* localStorage unavailable — leave hidden */
    }
  }
  });

  /* -------------------------------------------------------------------------
     Build-your-own-pouch builder. Assembles a pouch client-side, then commits
     it as one cart/add.js items[] call (the size's container variant plus every
     mini, grouped by a `_pouch_build` line property). Reuses refreshCartDrawer/
     openDrawer/formatMoney from above. See sections/pouch-builder.liquid. */
  initSection(function (builderScope) {
  var builder = builderScope.querySelector('[data-pouch-builder]');
  if (builder && initOnce(builder, 'pouch-builder')) {
    document.documentElement.classList.add('js-builder');

    var sizeButtons = Array.prototype.slice.call(builder.querySelectorAll('[data-size]'));
    var miniGrid = builder.querySelector('[data-mini-grid]');
    var miniCards = miniGrid ? Array.prototype.slice.call(miniGrid.querySelectorAll('[data-mini]')) : [];
    var summaryItems = builder.querySelector('[data-summary-items]');
    var summaryCount = builder.querySelector('[data-summary-count]');
    var summarySize = builder.querySelector('[data-summary-size]');
    var summarySubtotal = builder.querySelector('[data-summary-subtotal]');
    var summaryEmptyHint = builder.querySelector('[data-summary-empty]');
    var summaryMsg = builder.querySelector('[data-summary-msg]');
    var commitBtn = builder.querySelector('[data-commit]');
    var miniSearch = builder.querySelector('[data-mini-search]');
    var brandChips = builder.querySelector('[data-brand-chips]');
    var categoryChips = builder.querySelector('[data-category-chips]');
    var noResults = builder.querySelector('[data-no-results]');
    var defaultMax = parseInt(builder.getAttribute('data-default-max'), 10) || 8;
    var sizeRequired = sizeButtons.length > 0;

    var selectedSize = null; /* { label, max, variantId, price } */
    var chosen = {}; /* variantId -> { id, price, title, vendor, image, qty } */
    var cardById = {};
    miniCards.forEach(function (card) { cardById[card.getAttribute('data-variant-id')] = card; });

    function escapeHtml(value) {
      return String(value == null ? '' : value).replace(/[&<>"']/g, function (ch) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch];
      });
    }

    function currentMax() { return selectedSize ? selectedSize.max : defaultMax; }

    function totalItems() {
      return Object.keys(chosen).reduce(function (sum, id) { return sum + chosen[id].qty; }, 0);
    }

    function setCardState(card, on) {
      if (!card) return;
      card.classList.toggle('is-in-pouch', on);
      var button = card.querySelector('[data-add-mini]');
      if (button) button.textContent = on ? 'In pouch ✓' : 'Add to pouch';
    }

    function render() {
      var count = totalItems();
      var max = currentMax();

      if (summarySize) {
        if (selectedSize) {
          summarySize.textContent = selectedSize.label + (selectedSize.price ? ' · ' + formatMoney(selectedSize.price) + ' pouch' : '');
        } else {
          summarySize.textContent = sizeRequired ? 'No size selected' : 'Any minis';
        }
      }
      if (summaryCount) summaryCount.textContent = count + ' / ' + max + ' minis';

      var ids = Object.keys(chosen);
      if (summaryEmptyHint) summaryEmptyHint.hidden = ids.length > 0;
      var subtotal = selectedSize && selectedSize.price ? selectedSize.price : 0;
      var rows = '';
      ids.forEach(function (id) {
        var item = chosen[id];
        subtotal += item.price * item.qty;
        rows +=
          '<li class="pouch-builder__summary-item" data-line-id="' + escapeHtml(id) + '">' +
          '<span class="pouch-builder__summary-thumb">' +
          (item.image ? '<img src="' + escapeHtml(item.image) + '" alt="" loading="lazy" width="48" height="48">' : '') +
          '</span>' +
          '<span class="pouch-builder__summary-line">' +
          '<span class="pouch-builder__summary-name">' + escapeHtml(item.title) + '</span>' +
          (item.vendor ? '<span class="pouch-builder__summary-vendor">' + escapeHtml(item.vendor) + '</span>' : '') +
          '<span class="pouch-builder__summary-qty">' +
          '<button type="button" data-line-action="dec" aria-label="Decrease quantity">&minus;</button>' +
          '<span>' + item.qty + '</span>' +
          '<button type="button" data-line-action="inc" aria-label="Increase quantity">+</button>' +
          '</span>' +
          '</span>' +
          '<span class="pouch-builder__summary-right">' +
          '<span class="pouch-builder__summary-price">' + formatMoney(item.price * item.qty) + '</span>' +
          '<button type="button" class="pouch-builder__summary-remove" data-line-action="remove">Remove</button>' +
          '</span>' +
          '</li>';
      });
      if (summaryItems) summaryItems.innerHTML = rows;
      if (summarySubtotal) summarySubtotal.textContent = formatMoney(subtotal);

      /* A selected size with no variant id cannot be committed: the container line
         would be dropped and the order would not say which pouch to pack. */
      var sizeUsable = !!selectedSize && !!selectedSize.variantId;
      var canCommit = (!sizeRequired || sizeUsable) && count >= 1 && count <= max;
      if (commitBtn) commitBtn.disabled = !canCommit;

      var msg = '';
      if (count > max) msg = 'Pouch is full — remove ' + (count - max) + ' to fit ' + selectedSize.label + '.';
      else if (count === max && max > 0) msg = 'Pouch is full — max ' + max + ' minis.';
      else if (sizeRequired && !selectedSize && count > 0) msg = 'Pick a pouch size to check out.';
      else if (sizeRequired && selectedSize && !sizeUsable) msg = 'That pouch size is unavailable right now — pick another.';
      if (summaryMsg) {
        if (msg) {
          if (summaryMsg.textContent !== msg) summaryMsg.textContent = msg;
          summaryMsg.hidden = false;
        } else {
          summaryMsg.hidden = true;
        }
      }
    }

    function flashFull() {
      if (!summaryMsg || !allowMotion) return;
      summaryMsg.classList.remove('is-flash');
      void summaryMsg.offsetWidth;
      summaryMsg.classList.add('is-flash');
    }

    /* Size picker (radiogroup with roving tabindex + arrow keys) */
    sizeButtons.forEach(function (button, index) {
      button.setAttribute('tabindex', index === 0 ? '0' : '-1');
      function choose() {
        sizeButtons.forEach(function (other) {
          other.classList.remove('is-active');
          other.setAttribute('aria-checked', 'false');
          other.setAttribute('tabindex', '-1');
        });
        button.classList.add('is-active');
        button.setAttribute('aria-checked', 'true');
        button.setAttribute('tabindex', '0');
        selectedSize = {
          label: button.getAttribute('data-label') || 'Pouch',
          max: parseInt(button.getAttribute('data-max'), 10) || defaultMax,
          variantId: button.getAttribute('data-variant-id') || '',
          price: parseInt(button.getAttribute('data-price'), 10) || 0
        };
        render();
      }
      button.addEventListener('click', choose);
      button.addEventListener('keydown', function (event) {
        var next = null;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = (index + 1) % sizeButtons.length;
        else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = (index - 1 + sizeButtons.length) % sizeButtons.length;
        if (next !== null) { event.preventDefault(); sizeButtons[next].focus(); sizeButtons[next].click(); }
      });
    });

    /* Add / remove a mini from its card */
    if (miniGrid) {
      miniGrid.addEventListener('click', function (event) {
        var button = event.target.closest('[data-add-mini]');
        if (!button) return;
        var card = button.closest('[data-mini]');
        if (!card) return;
        var id = card.getAttribute('data-variant-id');
        if (!id) return;
        if (chosen[id]) {
          delete chosen[id];
          setCardState(card, false);
          render();
          return;
        }
        if (totalItems() >= currentMax()) { flashFull(); return; }
        chosen[id] = {
          id: id,
          price: parseInt(card.getAttribute('data-price'), 10) || 0,
          title: card.getAttribute('data-title') || '',
          vendor: card.getAttribute('data-vendor') || '',
          image: card.getAttribute('data-image') || '',
          qty: 1
        };
        setCardState(card, true);
        render();
      });
    }

    /* Quantity steppers / remove within the summary */
    if (summaryItems) {
      summaryItems.addEventListener('click', function (event) {
        var button = event.target.closest('[data-line-action]');
        if (!button) return;
        var row = button.closest('[data-line-id]');
        if (!row) return;
        var id = row.getAttribute('data-line-id');
        var item = chosen[id];
        if (!item) return;
        var action = button.getAttribute('data-line-action');
        if (action === 'inc') {
          if (totalItems() >= currentMax()) { flashFull(); return; }
          item.qty += 1;
        } else if (action === 'dec') {
          item.qty -= 1;
          if (item.qty <= 0) { delete chosen[id]; setCardState(cardById[id], false); }
        } else if (action === 'remove') {
          delete chosen[id];
          setCardState(cardById[id], false);
        }
        render();
      });
    }

    /* Filter chips (brand + category) built from the rendered minis */
    function activeValues(kind) {
      return Array.prototype.slice
        .call(builder.querySelectorAll('[data-filter="' + kind + '"][aria-pressed="true"]'))
        .map(function (chip) { return chip.getAttribute('data-value'); });
    }

    function applyFilter() {
      var brands = activeValues('brand');
      var cats = activeValues('category');
      var query = ((miniSearch && miniSearch.value) || '').trim().toLowerCase();
      var visible = 0;
      miniCards.forEach(function (card) {
        var vendor = (card.getAttribute('data-vendor') || '').toLowerCase();
        var type = (card.getAttribute('data-type') || '').toLowerCase();
        var title = (card.getAttribute('data-title') || '').toLowerCase();
        var ok = (!brands.length || brands.indexOf(vendor) !== -1) &&
          (!cats.length || cats.indexOf(type) !== -1) &&
          (!query || title.indexOf(query) !== -1 || vendor.indexOf(query) !== -1);
        card.hidden = !ok;
        if (ok) visible += 1;
      });
      if (noResults) noResults.hidden = visible !== 0;
    }

    function buildChips(container, values, kind) {
      if (!container || !values.length) return;
      values.forEach(function (value) {
        var chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'pouch-builder__chip';
        chip.setAttribute('aria-pressed', 'false');
        chip.setAttribute('data-filter', kind);
        chip.setAttribute('data-value', value.toLowerCase());
        chip.textContent = value;
        chip.addEventListener('click', function () {
          var on = chip.getAttribute('aria-pressed') === 'true';
          chip.setAttribute('aria-pressed', on ? 'false' : 'true');
          chip.classList.toggle('is-active', !on);
          applyFilter();
        });
        container.appendChild(chip);
      });
    }

    /* Add chips for any brands/categories not already represented (re-run after
       "Load more" pulls in cards from further pages). */
    function ensureChips() {
      var haveBrand = {};
      var haveCategory = {};
      builder.querySelectorAll('[data-brand-chips] .pouch-builder__chip').forEach(function (chip) {
        haveBrand[chip.getAttribute('data-value')] = true;
      });
      builder.querySelectorAll('[data-category-chips] .pouch-builder__chip').forEach(function (chip) {
        haveCategory[chip.getAttribute('data-value')] = true;
      });
      var newVendors = [];
      var newTypes = [];
      miniCards.forEach(function (card) {
        var vendor = card.getAttribute('data-vendor');
        var type = card.getAttribute('data-type');
        if (vendor && !haveBrand[vendor.toLowerCase()] && newVendors.indexOf(vendor) === -1) newVendors.push(vendor);
        if (type && !haveCategory[type.toLowerCase()] && newTypes.indexOf(type) === -1) newTypes.push(type);
      });
      newVendors.sort();
      newTypes.sort();
      buildChips(brandChips, newVendors, 'brand');
      buildChips(categoryChips, newTypes, 'category');
    }
    ensureChips();
    if (miniSearch) miniSearch.addEventListener('input', applyFilter);

    /* Load more minis: append the next page of the section (Section Rendering API)
       so browsing past one page needs no reload. The link is a real ?page= href,
       so it still works without JS. */
    builder.addEventListener('click', function (event) {
      var link = event.target.closest('[data-load-more]');
      if (!link) return;
      event.preventDefault();
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      var label = link.textContent;
      link.textContent = 'Loading…';
      var url = link.getAttribute('href');
      var fetchUrl = url + (url.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + encodeURIComponent(builder.getAttribute('data-section-id'));
      fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (response) {
          if (!response.ok) throw new Error('load more failed');
          return response.text();
        })
        .then(function (html) {
          var parser = document.createElement('div');
          parser.innerHTML = html;
          Array.prototype.slice.call(parser.querySelectorAll('[data-mini-grid] [data-mini]')).forEach(function (card) {
            miniGrid.appendChild(card);
            miniCards.push(card);
            cardById[card.getAttribute('data-variant-id')] = card;
          });
          ensureChips();
          applyFilter();
          var nextMore = parser.querySelector('[data-load-more]');
          var wrap = link.closest('.pouch-builder__more');
          if (nextMore) {
            link.setAttribute('href', nextMore.getAttribute('href'));
            link.textContent = label;
            link.removeAttribute('aria-busy');
          } else if (wrap) {
            wrap.remove();
          }
        })
        .catch(function () {
          link.textContent = label;
          link.removeAttribute('aria-busy');
          window.location.href = url;
        });
    });

    /* Commit: one cart/add.js with the container variant + every mini */
    function commitPouch() {
      if (!commitBtn || commitBtn.disabled) return;
      /* Refuse rather than quietly omit the container line. Without it the order
         carries the minis but nothing recording which pouch to pack, and the
         warehouse cannot fulfil it. This used to fall through the `if` below and
         add the minis anyway. */
      if (sizeRequired && (!selectedSize || !selectedSize.variantId)) {
        if (summaryMsg) {
          summaryMsg.textContent = 'That pouch size is unavailable right now — pick another.';
          summaryMsg.hidden = false;
        }
        return;
      }
      var groupId = 'pouch-' + Date.now().toString(36) + '-' + Math.floor(Math.random() * 1e6).toString(36);
      var items = [];
      if (selectedSize && selectedSize.variantId) {
        items.push({
          id: selectedSize.variantId,
          quantity: 1,
          properties: { _pouch_build: groupId, _pouch_role: 'pouch' }
        });
      }
      Object.keys(chosen).forEach(function (id) {
        items.push({
          id: id,
          quantity: chosen[id].qty,
          properties: { _pouch_build: groupId, _pouch_role: 'item' }
        });
      });
      if (!items.length) return;

      var previousLabel = commitBtn.textContent;
      commitBtn.disabled = true;
      commitBtn.textContent = 'Adding…';
      var cartRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      fetch(cartRoot + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (response) {
        return response.json().then(function (data) { return { ok: response.ok, data: data }; });
      }).then(function (result) {
        commitBtn.textContent = previousLabel;
        if (!result.ok) {
          if (summaryMsg) {
            summaryMsg.textContent = (result.data && (result.data.description || result.data.message)) || 'Sorry, your pouch could not be added.';
            summaryMsg.hidden = false;
          }
          commitBtn.disabled = false;
          return;
        }
        /* Clear the minis (keep the size for a quick second build) and open the drawer */
        chosen = {};
        miniCards.forEach(function (card) { setCardState(card, false); });
        try { sessionStorage.setItem('100ml:cart-deadline', String(Date.now() + reservationMs())); } catch (error) { /* ignore */ }
        render();
        refreshCartDrawer(true);
      }).catch(function () {
        commitBtn.textContent = previousLabel;
        if (summaryMsg) {
          summaryMsg.textContent = 'Something went wrong. Please try again.';
          summaryMsg.hidden = false;
        }
        commitBtn.disabled = false;
      });
    }

    if (commitBtn) commitBtn.addEventListener('click', commitPouch);

    render();
  }
  });
})();
